/**
 * SpreadJS Engine for browser-based evaluation
 *
 * This module evaluates spreadsheet formulas using SpreadJS in the browser.
 * Used for frontend validation and engine comparison.
 */

import type { SheetSpec } from '@meridian-ssa/shared';

import { logError } from '@/lib/logger';

// Dynamic import of SpreadJS (browser environment)
let GC: any = null;

export interface SpreadJSEngineResult {
  computedValues: any[][][]; // [sheetIndex][row][col]
  errorCells: string[]; // Array of cell addresses like "Sheet1!A5"
  errors: string[]; // Array of error messages
  detailedErrors: Array<{
    cell: string;
    errorType: string;
    formula?: string;
    message?: string;
  }>;
}

/**
 * Helper function to parse A1 notation to row/col indices
 */
function parseA1(a1: string): { row: number; col: number } {
  const match = a1.match(/^([A-Z]+)(\d+)$/);
  if (!match) throw new Error(`Invalid A1 notation: ${a1}`);

  const colStr = match[1];
  const rowStr = match[2];

  // Convert column letters to number (A=0, B=1, ..., Z=25, AA=26, etc.)
  let col = 0;
  for (let i = 0; i < colStr.length; i++) {
    col = col * 26 + (colStr.charCodeAt(i) - 65 + 1);
  }
  col -= 1; // Make it 0-indexed

  const row = parseInt(rowStr, 10) - 1; // Make it 0-indexed

  return { row, col };
}

/**
 * Calculate sheet dimensions from cell data
 */
function calculateSheetDimensions(cells: any[]): {
  rows: number;
  cols: number;
} {
  if (!cells || cells.length === 0) {
    return { rows: 0, cols: 0 };
  }

  let maxRow = 0;
  let maxCol = 0;

  for (const cell of cells) {
    try {
      const { row, col } = parseA1(cell.a1);
      maxRow = Math.max(maxRow, row);
      maxCol = Math.max(maxCol, col);
    } catch (e) {
      // Skip invalid cell references
      continue;
    }
  }

  return { rows: maxRow + 1, cols: maxCol + 1 };
}

/**
 * Convert col index to letter (0 = A, 1 = B, etc.)
 */
function colToLetter(col: number): string {
  let result = '';
  let c = col;
  while (c >= 0) {
    result = String.fromCharCode(65 + (c % 26)) + result;
    c = Math.floor(c / 26) - 1;
  }
  return result;
}

/**
 * Get human-readable error message for error codes
 */
function getErrorMessage(errorCode: string): string {
  const errorMessages: Record<string, string> = {
    '#NULL!':
      'Null intersection - formula refers to an intersection of two ranges that do not intersect',
    '#DIV/0!': 'Division by zero',
    '#VALUE!': 'Wrong type of operand or argument',
    '#REF!': 'Invalid cell reference',
    '#NAME?': 'Unrecognized name in formula',
    '#NUM!': 'Invalid numeric value',
    '#N/A': 'Value not available',
    '#GETTING_DATA': 'Waiting for data (should not appear in final results)',
    '#CALC!': 'Calculation error',
  };

  return errorMessages[errorCode] || 'Unknown error';
}

/**
 * Evaluate a SheetSpec using SpreadJS's calculation engine in the browser
 * This function is async to allow for dynamic import of SpreadJS
 */
export async function evaluateSpreadJSAsync(
  sheetSpec: SheetSpec
): Promise<SpreadJSEngineResult> {
  try {
    // Load SpreadJS if not already loaded
    if (!GC) {
      GC = await import('@mescius/spread-sheets');
    }

    // Create a headless workbook (no DOM element needed for calculation)
    const workbook = new GC.Spread.Sheets.Workbook();

    const computedValues: any[][][] = [];
    const errorCells: string[] = [];
    const errors: string[] = [];
    const detailedErrors: Array<{
      cell: string;
      errorType: string;
      formula?: string;
      message?: string;
    }> = [];

    // IMPORTANT: Create all sheets first BEFORE populating cells
    // This prevents #REF! errors when formulas reference sheets that haven't been created yet
    const sheets: any[] = [];
    sheetSpec.sheets.forEach((sheetData, sheetIndex) => {
      const sheet = new GC.Spread.Sheets.Worksheet(sheetData.name);
      workbook.addSheet(sheetIndex, sheet);
      sheets.push(sheet);
    });

    // Now populate cells in all sheets (after all sheets exist)
    sheetSpec.sheets.forEach((sheetData, sheetIndex) => {
      const sheet = sheets[sheetIndex];

      // Load cells
      sheetData.cells.forEach(cell => {
        try {
          const { row, col } = parseA1(cell.a1);

          if (cell.type === 'formula' && cell.formula) {
            const formula = cell.formula.startsWith('=')
              ? cell.formula.substring(1)
              : cell.formula;

            try {
              sheet.setFormula(row, col, formula);
            } catch (formulaError: any) {
              // Track invalid formula for diagnostics
              const cellA1 = `${sheetData.name}!${cell.a1}`;
              errorCells.push(cellA1);
              errors.push(
                `Invalid formula in cell ${cell.a1}: ${formulaError.message || formulaError}`
              );

              // Set #INVALID as the cell value
              sheet.setValue(row, col, '#INVALID');

              detailedErrors.push({
                cell: cellA1,
                errorType: '#INVALID',
                formula: `=${formula}`,
                message: `Formula syntax error: ${formulaError.message || formulaError}`,
              });
            }
          } else if (cell.value !== undefined) {
            sheet.setValue(row, col, cell.value);
          }
        } catch (e) {
          errors.push(`Failed to load cell ${cell.a1}: ${e}`);
        }
      });

      // Apply named ranges if present
      if (sheetData.namedRanges) {
        sheetData.namedRanges.forEach(namedRange => {
          try {
            workbook.addCustomName(
              namedRange.name,
              namedRange.ref,
              0,
              sheetIndex
            );
          } catch (e) {
            errors.push(`Failed to add named range ${namedRange.name}: ${e}`);
          }
        });
      }

      // Force recalculation
      workbook.calculate();

      // Calculate dimensions from actual cell data
      const { rows, cols } = calculateSheetDimensions(sheetData.cells);

      // Extract computed values and detect errors
      const sheetValues: any[][] = [];
      for (let row = 0; row < rows; row++) {
        const rowValues: any[] = [];
        for (let col = 0; col < cols; col++) {
          const value = sheet.getValue(row, col);
          rowValues.push(value);

          // Check for error values
          if (value != null && typeof value === 'object' && value.toString) {
            const valueStr = value.toString();
            if (valueStr.startsWith('#')) {
              const cellA1 = `${sheetData.name}!${colToLetter(col)}${row + 1}`;
              errorCells.push(cellA1);

              // Get formula if this is a formula cell
              const formula = sheet.getFormula(row, col);

              detailedErrors.push({
                cell: cellA1,
                errorType: valueStr,
                formula: formula ? `=${formula}` : undefined,
                message: getErrorMessage(valueStr),
              });
            }
          } else if (typeof value === 'string' && value.startsWith('#')) {
            // Handle string error values
            const cellA1 = `${sheetData.name}!${colToLetter(col)}${row + 1}`;
            errorCells.push(cellA1);

            const formula = sheet.getFormula(row, col);

            detailedErrors.push({
              cell: cellA1,
              errorType: value,
              formula: formula ? `=${formula}` : undefined,
              message: getErrorMessage(value),
            });
          }
        }
        sheetValues.push(rowValues);
      }
      computedValues.push(sheetValues);
    });

    // Clean up
    workbook.destroy();

    return {
      computedValues,
      errorCells,
      errors,
      detailedErrors,
    };
  } catch (error: any) {
    // If evaluation fails entirely, return error state
    logError('SpreadJS browser evaluation error', error);
    return {
      computedValues: [],
      errorCells: [],
      errors: [`SpreadJS evaluation failed: ${error.message || error}`],
      detailedErrors: [],
    };
  }
}
